import {FC, useEffect, useState, FocusEvent, useMemo, useRef} from 'react';
import {toast} from 'react-toastify';
//mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
//store
import {getCurrentEventSelector} from '@/store/organization-event';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {clearPrivateTicketsTiers, getTicketsTiers} from '@/store/organization-tables';
//components
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Input from '@/shared/components/Inputs/Input';
import Modal from '@/shared/components/Modal';
import TableDetails from '@/components/Organization/TablesAndTickets/TableDetails';
import AddTableTicket from '@/components/Organization/TablesAndTickets/AddTableTicket';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import {ReactComponent as TicketIcon} from '@icons/sidebar/ticket.svg';
//services
import {OrganizationTableAndTicketsService} from '@/utils/services';
//constants
import {
  BODY1,
  CONTACT_PHONE,
  PRICE,
  PURCHASER,
  RIGHT,
  TABLE,
  TABLE_PURCHASER,
  defaultPage,
  defaultPageSize,
  NUMBER,
  PAY_STATUS,
  TICKET_TYPE,
  TABLE_NAME,
  HASH,
  DETAILS,
  DELETE_UPPER,
} from '@/utils/constants';
//enums
import {MENU_OPTION, TABLE_STATUS} from '@/utils/enums/tables.enum';
import {ROUTER_URL} from '@/utils/enums';
//helpers
import {getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
import {currencyFormatter} from '@/utils/helpers/currency';
//hooks
import {getUserSelector, getUsersTablesAndTickets, getUserTablesSelector} from '@/store/user';
import {useNavigate, useParams} from 'react-router-dom';
//styles
import {myTablesAndTicketsTableStyle} from './MyTablesAndTicketsStyles.styles';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {IOrganizationTicketsListData, PaymentResult} from '@/utils/types';
import {userAPI} from '@/core/api';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

type SortColumn = 'purchaser' | 'price' | 'number';
type SortOrder = 'asc' | 'desc';

export const myTableTicketsMenuContent = [DETAILS, DELETE_UPPER];

const MyTablesAndTicketsTable: FC = ({slugFromProps}: {slugFromProps?: string}) => {
  const {slug: slugFromUrl} = useParams();
  const slug = slugFromUrl || slugFromProps;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {apiInstance} = useApiInstance();

  const [currentPage, setCurrentPage] = useState<number>(defaultPage);
  const [searchName, setSearchName] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
  const [isShowTicketDetailsModal, setIsShowTicketDetailsModal] = useState<boolean>(false);
  const [detailsTicketId, setDetailsTicketId] = useState<number>();
  const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);

  const user = useAppSelector(getUserSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const userTables = useAppSelector(getUserTablesSelector);

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const handleAttentionModal = () => setIsAttentionModalOpen(!isAttentionModalOpen);

  const filtered = useMemo(() => {
    if (!searchName.trim()) return userTables;
    const lower = searchName.toLowerCase();
    return userTables.filter(
      (row) =>
        row.typeOfSeat?.toLowerCase().includes(lower) ||
        row.name?.toLowerCase().includes(lower) ||
        row.name?.toLowerCase().includes(lower) ||
        row.price?.toString().toLowerCase().includes(lower) ||
        row.number?.toString().toLowerCase().includes(lower)
    );
  }, [userTables, searchName]);

  const sorted = useMemo(() => {
    if (!sortColumn) return filtered;
    return [...filtered].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortColumn) {
        case 'purchaser':
          aVal = a.name ?? '';
          bVal = b.name ?? '';
          break;
        case 'price':
          aVal = a.price ?? 0;
          bVal = b.price ?? 0;
          break;
        case 'number':
          aVal = a.number ?? 0;
          bVal = b.number ?? 0;
          break;
        default:
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortColumn, sortOrder]);

  const totalCount = sorted.length;
  const pageSize = defaultPageSize;

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(defaultPage);
  }, [searchName, sortColumn, sortOrder]);

  const handleSortChange = (column: SortColumn) => () => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortDirection = (column: SortColumn) => (sortColumn === column ? sortOrder : undefined);

  const handleAddTicketModalChange = () => {
    if (!currentEvent?.tableInfos.length) {
      handleAttentionModal();
      return;
    }

    const navigateTo = `/${slug}/${ROUTER_URL.USER_PURCHASE_TICKETS}`;
    if (!isShowAddTicketModal) getTableData();

    if (isLaptop) {
      setIsShowAddTicketModal((prev) => !prev);
    } else {
      navigate(navigateTo);
    }
  };

  const handleOpenTicketDetailsModal = (tableId?: number) => {
    setIsShowTicketDetailsModal((prev) => !prev);
    if (tableId) setDetailsTicketId(tableId);
  };

  const handleSearchChange = (value: string) => {
    setSearchName(value);
  };

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
  };

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

  const setTableNumber = async (newNumber: string, tableId: number): Promise<boolean> => {
    try {
      await OrganizationTableAndTicketsService.updateTableNumber({id: tableId, number: newNumber}, apiInstance);
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    }
  };

  const handleUpdateTableNumber = async (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    row: IOrganizationTicketsListData
  ) => {
    const input = event.target;
    const oldValue = String(row.number ?? '');

    if (input.value === oldValue) return;

    const ok = await setTableNumber(input.value, row.id);

    if (ok) {
      await getUserTables();
    } else {
      input.value = oldValue;
    }
  };

  const handleDeleteTable = async (tableId: number) => {
    try {
      await OrganizationTableAndTicketsService.deleteTableAndTickets(tableId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleClickMenuOption = (option: string, tableId: number, item: IOrganizationTicketsListData) => async () => {
    const navigateTo = `/${slug}/${ROUTER_URL.USER_TABLES_DETAILS}/${tableId}`;
    switch (option) {
      case MENU_OPTION.DELETE:
        await handleDeleteTable(tableId);
        break;

      case MENU_OPTION.DETAILS:
        isLaptop ? handleOpenTicketDetailsModal(tableId) : navigate(navigateTo);
        break;

      default:
        break;
    }
  };

  const getTableData = async () => {
    if (!currentEvent?.id) return;

    try {
      await dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: userAPI})).unwrap();
      dispatch(clearPrivateTicketsTiers());
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleNavigate = (id: number) => {
    const navigateTo = `/${slug}/${ROUTER_URL.USER_TABLES_DETAILS}/${id}`;

    isLaptop ? handleOpenTicketDetailsModal(id) : navigate(navigateTo);
  };

  const getUserTables = async () => {
    if (!user || !currentEvent) return;
    try {
      await dispatch(getUsersTablesAndTickets({userId: user.id, eventId: currentEvent.id})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const MAX_RETRIES = 5;

  const checkPaymentStatus = async (paymentId: string, retryCount = 0, toastId?: string | number) => {
    if (retryCount === 0) {
      toastId = toast.loading('Checking payment status...');
    }

    try {
      const {data} = await OrganizationStripeService.checkPaymentStatus(+paymentId, userAPI);

      if (data.status === PaymentResult.COMPLETED) {
        if (toastId) toast.dismiss(toastId);
        toast.success('Payment completed successfully!');
        getUserTables();
      } else if (data.status === PaymentResult.PENDING) {
        if (retryCount < MAX_RETRIES) {
          timeoutRef.current = setTimeout(() => {
            checkPaymentStatus(paymentId, retryCount + 1, toastId);
          }, 3000);
        } else {
          if (toastId) toast.dismiss(toastId);
          toast.info('Payment is still being processed. Please check the status in a few minutes.');
        }
      } else if (data.status === PaymentResult.FAILED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment failed!');
      } else if (data.status === PaymentResult.REFUNDED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment refunded!');
      }
    } catch (error) {
      if (toastId) toast.dismiss(toastId);
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('paymentId');

    if (paymentId) {
      checkPaymentStatus(paymentId);

      params.delete('paymentId');
      params.delete('session_id');
      params.delete('organizationId');

      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Box sx={myTablesAndTicketsTableStyle.wrapper}>
      <SearchHeader
        page={currentPage}
        items={Math.min(totalCount, pageSize)}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        onFilterChange={() => {}}
        parentClassName={{padding: '0 16px'}}
        actionButton={
          <Button
            onClick={handleAddTicketModalChange}
            title="Buy Tickets"
            startIcon={<TicketIcon />}
            sx={myTablesAndTicketsTableStyle.searchButton}
            disabled={!user}
          />
        }
      />

      {paginated.length > 0 ? (
        <Box sx={myTablesAndTicketsTableStyle.tableContent}>
          <TableContainer sx={myTablesAndTicketsTableStyle.container}>
            <Table sx={myTablesAndTicketsTableStyle.table}>
              <TableHead>
                <TableRow sx={myTablesAndTicketsTableStyle.tableHeader}>
                  <TableCell>{TICKET_TYPE}</TableCell>

                  <TableCell>{TABLE_NAME}</TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === PURCHASER}
                      direction={getSortDirection('purchaser')}
                      onClick={handleSortChange('purchaser')}
                      IconComponent={UpDownIcon}
                    >
                      {TABLE_PURCHASER}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>{CONTACT_PHONE}</TableCell>

                  <TableCell>{PRICE}</TableCell>

                  <TableCell>{PAY_STATUS}</TableCell>

                  <TableCell align={RIGHT}>
                    <TableSortLabel
                      active={sortColumn === NUMBER}
                      direction={getSortDirection('number')}
                      onClick={handleSortChange('number')}
                      IconComponent={UpDownIcon}
                    >
                      {TABLE}
                      {HASH}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((row) => (
                  <TableRow key={row.id} sx={myTablesAndTicketsTableStyle.tableBody}>
                    <TableCell onClick={() => handleNavigate(row.id)}>{row.typeOfSeat}</TableCell>

                    <TableCell onClick={() => handleNavigate(row.id)}>{row.name}</TableCell>

                    <TableCell onClick={() => handleNavigate(row.id)}>
                      <Box>
                        <Typography>{row.purchaser}</Typography>
                        <Typography sx={{wordBreak: 'break-word', whiteSpace: 'normal'}}>{row.email}</Typography>
                      </Box>
                    </TableCell>

                    <TableCell onClick={() => handleNavigate(row.id)}>{row.contactPhone}</TableCell>

                    <TableCell onClick={() => handleNavigate(row.id)}>{currencyFormatter.format(row.price)}</TableCell>

                    <TableCell onClick={() => handleNavigate(row.id)}>
                      <Typography sx={getPayStatusSx(row.isPaid ? TABLE_STATUS.PAID : TABLE_STATUS.PENDING)}>
                        {row.isPaid ? TABLE_STATUS.PAID : TABLE_STATUS.PENDING}
                        <EllipseIcon />
                      </Typography>
                    </TableCell>

                    <TableCell align={RIGHT}>
                      <Input
                        key={`${row.id}-${row.number}`}
                        defaultValue={row.number}
                        onBlur={(event) => handleUpdateTableNumber(event, row)}
                        type={NUMBER}
                      />
                    </TableCell>

                    <TableCell>
                      <ActionsMenu
                        disableRipple
                        content={
                          <Box>
                            {myTableTicketsMenuContent
                              .filter((item) => item !== MENU_OPTION.MARK_AS_PAID)
                              .map((item) => (
                                <Typography
                                  key={item}
                                  onClick={handleClickMenuOption(item, row.id, row)}
                                  variant={BODY1}
                                  sx={{
                                    ...myTablesAndTicketsTableStyle.menuItem,
                                    ...(item === MENU_OPTION.MARK_AS_PAID &&
                                      row.isPaid &&
                                      myTablesAndTicketsTableStyle.disabledMenuItem),
                                  }}
                                >
                                  {item}
                                </Typography>
                              ))}
                          </Box>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={myTablesAndTicketsTableStyle.pagination}>
            <Pagination
              page={currentPage}
              pageSize={pageSize}
              total={totalCount || 1}
              onChange={(step) => setCurrentPage(step)}
            />
          </Box>
        </Box>
      ) : (
        <EmptyTableData text={userTables.length ? 'No data found' : 'You do not have any tables or tickets'} />
      )}

      <Modal
        onClose={handleAddTicketModalChange}
        open={isShowAddTicketModal}
        sx={myTablesAndTicketsTableStyle.modal}
        title="Buy Tickets"
      >
        <AddTableTicket onCloseModal={handleAddTicketModalChange} onSuccess={getUserTables} />
      </Modal>

      <Modal
        onClose={() => handleOpenTicketDetailsModal()}
        open={isShowTicketDetailsModal}
        sx={myTablesAndTicketsTableStyle.modal}
      >
        <TableDetails
          onCloseModal={() => handleOpenTicketDetailsModal()}
          currentTableId={detailsTicketId}
          onSuccess={getUserTables}
        />
      </Modal>

      <Modal
        open={isAttentionModalOpen}
        onClose={handleAttentionModal}
        title="Table Tiers Coming Soon"
        description="The organizer hasn`t set up the table tiers yet. Please check back later"
        sx={myTablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default MyTablesAndTicketsTable;
